import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import { useSessionUser } from '@/hooks/use-session'
import * as v from 'valibot'
import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { Test } from '@/routes/_app/-components/applicants/test'
import { RegistrationSteps } from '@/routes/_app/-components/shared/registration-steps'
import { useAppStore } from '@/lib/app-store'
import { flowViewTransition, withViewTransition } from '@/lib/utils'
import { queryClient, trpc, type User } from '@/main'
import { FlowFrame } from '@/routes/_app/-components/shared/flow-frame'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { TestStepPending } from '@/routes/_app/-components/shared/flow-pending'

export const Route = createFileRoute('/_app/test/competences')({
  validateSearch: v.object({
    retake: v.optional(v.boolean(), false),
    resume: v.optional(v.boolean()),
    // Carried through onboarding so the final landing can return to the visitor's intended destination.
    redirect: v.optional(v.string()),
  }),
  // pending: rekey — `retake` only flips when a NEW test starts, a genuine screen change the skeleton belongs to.
  loaderDeps: ({ search }) => ({ retake: search.retake }),
  context: ({ deps }) => ({
    // NO `gcTime`/`staleTime` overrides here, deliberately: on route context they apply to the PREFETCH, where
    // a query with no observers is swept the macrotask after it settles, before `useSuspenseQuery` subscribes.
    // (`Query.updateGcTime` keeps the MAX, so a `gcTime: 0` never shortened what the loader had already made
    // with the default 5 minutes.) See the note in `Test` about what re-entry actually seeds from.
    testQuestionsQueryOptions: trpc.applicants.getQuestions.queryOptions({
      type: 'competences',
      newTest: deps.retake,
    }),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.testQuestionsQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: TestStepPending,
})

function RouteComponent() {
  const context = Route.useRouteContext()
  const { data: questionsResponse } = useSuspenseQuery(context.testQuestionsQueryOptions)
  const navigate = useNavigate()
  const { retake, redirect } = Route.useSearch()
  const user = useSessionUser()

  // Logged-out → /login is the `_app` layout guard's job (route-access), before `getQuestions` mounts here.
  // The competences set depends on whether the applicant has had people in their charge, and the server
  // withholds questions until that is set. The registration form normally collects it, but a social login
  // auto-registers without it, so it becomes its own step ahead of the test.
  if (user?.type === 'applicant' && user.peopleInCharge == null) {
    return <PeopleInChargeGate user={user} />
  }

  return (
    <Test
      questionsResponse={questionsResponse}
      type="competences"
      // `retake` travels FORWARD only: it is what asks `getQuestions` for a fresh answer row, and without it
      // the next test resumes on the previous run's triad 36/36. Never backward — there the finished test
      // would be reset by the same flag.
      nextStep={() =>
        navigate({ to: '/test/values', search: { redirect, retake }, viewTransition: flowViewTransition() })
      }
      previousStep={() => navigate({ to: '/test', search: { redirect } })}
      newTest={retake}
    />
  )
}

// The "people in charge" step — shown only while the answer is missing, right after the welcome. On
// answer it persists `peopleInCharge`, refetches the user, and the route re-renders straight into the
// competences test (the next step) rather than back to the welcome screen.
function PeopleInChargeGate(props: { user: User }) {
  const { t } = useLingui()
  const [pending, setPending] = useState<boolean | null>(null)
  const markPeopleStepShown = useAppStore((state) => state.markPeopleStepShown)
  const { mutateAsync: updateUser } = useMutation(trpc.auth.updateUser.mutationOptions())

  // Latch the step so it stays visible (as completed) in the indicator once answered.
  useEffect(() => {
    markPeopleStepShown()
  }, [markPeopleStepShown])

  const save = async (peopleInCharge: boolean) => {
    if (pending != null) return
    setPending(peopleInCharge)
    try {
      await updateUser({ peopleInCharge })
      // The loader ran while peopleInCharge was null, so `getQuestions` is cached as the value-shaped
      // `{ status: 'profile-not-defined' }` and <Test> renders null for any non-'ok' status — the
      // questionnaire would be blank until the refetch lands. The global revalidation already started one
      // (lib/revalidate); this AWAITS it, and `cancelRefetch: false` joins it instead of starting a second.
      await queryClient.invalidateQueries(trpc.applicants.getQuestions.queryFilter(), { cancelRefetch: false })
      // This is a SAME-ROUTE data swap (not a navigation), so drive the transition ourselves: flip the
      // cached user inside a view transition and the route re-renders straight into the competences
      // <Test> with the same box/header morph as every other onboarding step. The global revalidation's
      // own `getUser` refetch reconciles it in the background.
      withViewTransition(() => {
        queryClient.setQueryData(trpc.auth.getUser.queryKey(), (old) => (old ? { ...old, peopleInCharge } : old))
      })
    } catch {
      setPending(null)
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="mx-auto flex w-full max-w-[480px] flex-col items-center gap-6 px-4 py-6">
        <RegistrationSteps user={props.user} stage={{ kind: 'people' }} />
        <div className="flow-header text-center">
          <h1 className="text-2xl font-semibold">{t`One quick question`}</h1>
          <p className="text-muted-foreground mt-1">{t`We'll use this to tailor the questionnaire to your experience`}</p>
        </div>

        <FlowFrame contentClassName="flex flex-col gap-5">
          <p className="text-center text-base font-medium">{t`Have you had people in your charge?`}</p>
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={() => void save(false)}
              disabled={pending != null}
              className="h-12 text-base font-semibold"
            >
              {pending === false && <Spinner />}
              {t`No`}
            </Button>
            <Button onClick={() => void save(true)} disabled={pending != null} className="h-12 text-base font-semibold">
              {pending === true && <Spinner />}
              {t`Yes`}
            </Button>
          </div>
        </FlowFrame>
      </div>
    </section>
  )
}
